/**
 * Uploading a Notice's attachments: checks each file with the attachment rules,
 * then puts it in storage through a presigned URL. Add Event and Planner rows
 * both upload with this.
 */
import { api } from "./api";
import type { NoticeUploadedFile } from "../domain/notices/noticePayload";
import {
  NOTICE_ATTACHMENTS_MAX,
  noticeFileSizeProblem,
  noticeFileTypeProblem,
} from "../domain/notices/noticeAttachments";
import { processImageForUpload, shouldProcessNoticeImage } from "../utils/imageUpload";
import {
  formatNoticeUploadStatus,
  PresignedUploadAbortError,
  uploadFileToPresignedUrl,
  type NoticeUploadProgressState,
} from "../utils/presignedUpload";

type Notify = (message: { message: string; severity: "error" | "warning" | "info" }) => void;

export type UploadNoticeFilesOptions = {
  organizationId: string | undefined;
  token: string | undefined;
  /** Image notices allow larger images (25MB). */
  isImageNotice: boolean;
  /** How many attachments the Notice has already. */
  existingCount: number;
  signal: AbortSignal;
  notify: Notify;
  onStatus: (message: string) => void;
  onProgress: (progress: NoticeUploadProgressState) => void;
};

/**
 * Checks and uploads the files, telling the Team member about any that can't be
 * added. Returns the uploaded files, or none if cancelled or nothing could be
 * uploaded. Throws if an upload fails, after saying so.
 */
export async function uploadNoticeFiles(
  files: File[],
  {
    organizationId,
    token,
    isImageNotice,
    existingCount,
    signal,
    notify,
    onStatus,
    onProgress,
  }: UploadNoticeFilesOptions,
): Promise<NoticeUploadedFile[]> {
  const validatedFiles: File[] = [];

  for (const file of files) {
    const typeProblem = noticeFileTypeProblem(file);
    if (typeProblem) {
      notify({ message: typeProblem, severity: "warning" });
      continue;
    }

    let fileToUpload = file;
    if (shouldProcessNoticeImage(file)) {
      try {
        onStatus("Optimizing image...");
        fileToUpload = await processImageForUpload(file);
      } catch (error) {
        console.warn(
          `[uploadNoticeFiles] Failed to normalize image "${file.name}", uploading original instead.`,
          error,
        );
      }
    }

    const sizeProblem = noticeFileSizeProblem(file, fileToUpload, isImageNotice);
    if (sizeProblem) {
      notify({ message: sizeProblem, severity: "warning" });
      continue;
    }

    validatedFiles.push(fileToUpload);
  }

  if (!organizationId) {
    notify({
      message: "We couldn’t find your organization. Please refresh and log in again.",
      severity: "error",
    });
    return [];
  }

  const remainingSlots = NOTICE_ATTACHMENTS_MAX - existingCount;
  if (remainingSlots <= 0) {
    notify({
      message: `Maximum ${NOTICE_ATTACHMENTS_MAX} attachments allowed. Remove a file first.`,
      severity: "warning",
    });
    return [];
  }

  const filesToUpload = validatedFiles.slice(0, remainingSlots);
  if (validatedFiles.length > remainingSlots) {
    notify({
      message: `Only ${remainingSlots} more file${remainingSlots > 1 ? "s" : ""} can be added (max ${NOTICE_ATTACHMENTS_MAX}).`,
      severity: "info",
    });
  }

  const uploadedFiles: NoticeUploadedFile[] = [];
  const fileCount = filesToUpload.length;
  for (let fileIndex = 0; fileIndex < fileCount; fileIndex++) {
    const file = filesToUpload[fileIndex];
    if (signal.aborted) return [];

    const reportProgress = (percent: number) => {
      onProgress({ percent, fileName: file.name, fileIndex: fileIndex + 1, fileCount });
      onStatus(formatNoticeUploadStatus(file.name, percent, fileIndex + 1, fileCount));
    };

    try {
      reportProgress(0);
      const { presignedUrl, fileUrl } = await api.notices.getPresignedUrl(
        file.name,
        file.type,
        organizationId,
        undefined,
        token || undefined,
      );
      if (signal.aborted) return [];

      await uploadFileToPresignedUrl({
        presignedUrl,
        file,
        contentType: file.type,
        signal,
        onProgress: ({ percent }) => reportProgress(percent),
      });

      uploadedFiles.push({ fileUrl, fileName: file.name, contentType: file.type, size: file.size });
    } catch (error: unknown) {
      if (error instanceof PresignedUploadAbortError) {
        notify({ message: "Upload cancelled.", severity: "info" });
        return [];
      }
      console.error("Error uploading file via presigned URL:", error);
      notify({ message: `Failed to upload ${file.name}. Please try again.`, severity: "error" });
      throw error;
    }
  }

  return signal.aborted ? [] : uploadedFiles;
}
